import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import {
  globalSearchApi,
  type GlobalSearchAccess,
  type GlobalSearchResultType,
} from "@/core/api/globalSearchApi";
import { AppIcon, type AppIconName } from "@/shared/components/AppIcon";

interface GlobalCrmSearchProps extends GlobalSearchAccess {
  className?: string;
  autoFocus?: boolean;
  onResultSelected?: () => void;
}

const iconByType: Record<GlobalSearchResultType, AppIconName> = {
  lead: "trend",
  contact: "user",
  account: "building",
};

const titleByType: Record<GlobalSearchResultType, string> = {
  lead: "سرنخ‌ها",
  contact: "مخاطبان",
  account: "سازمان‌ها",
};

const useDebouncedValue = (value: string, delay = 260): string => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timer);
  }, [delay, value]);
  return debouncedValue;
};

export const GlobalCrmSearch = ({
  canSearchLeads,
  canSearchContacts,
  canSearchAccounts,
  className,
  autoFocus = false,
  onResultSelected,
}: GlobalCrmSearchProps): React.ReactElement => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const term = useDebouncedValue(value.trim());
  const access = useMemo(
    () => ({ canSearchLeads, canSearchContacts, canSearchAccounts }),
    [canSearchAccounts, canSearchContacts, canSearchLeads],
  );
  const hasSearchableModule =
    canSearchLeads || canSearchContacts || canSearchAccounts;
  const searchQuery = useQuery({
    queryKey: ["global-crm-search", term, access],
    queryFn: () => globalSearchApi.search(term, access),
    enabled: hasSearchableModule && term.length >= 2,
    staleTime: 15_000,
  });
  const results = searchQuery.data?.items ?? [];
  const groups = useMemo(
    () =>
      (["lead", "contact", "account"] as GlobalSearchResultType[])
        .map((type) => ({
          type,
          items: results.filter((result) => result.type === type),
        }))
        .filter((group) => group.items.length > 0),
    [results],
  );

  const selectResult = (path: string): void => {
    navigate(path);
    setValue("");
    setIsOpen(false);
    onResultSelected?.();
  };

  useEffect(() => {
    if (!autoFocus) return;
    setIsOpen(true);
    const timer = window.setTimeout(() => inputRef.current?.focus(), 80);
    return () => window.clearTimeout(timer);
  }, [autoFocus]);

  const closeWhenLeaving = (event: React.FocusEvent<HTMLDivElement>): void => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      window.setTimeout(() => setIsOpen(false), 100);
    }
  };

  return (
    <div className={`relative ${className ?? ""}`} onBlur={closeWhenLeaving}>
      <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-400 focus-within:border-brand-300 focus-within:bg-white focus-within:ring-4 focus-within:ring-brand-500/10">
        <AppIcon name="search" size={17} />
        <input
          ref={inputRef}
          aria-autocomplete="list"
          aria-expanded={isOpen && value.trim().length > 0}
          className="w-40 bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400 xl:w-60"
          onChange={(event) => {
            setValue(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setValue("");
              setIsOpen(false);
            }
            if (event.key === "Enter" && results[0]) {
              event.preventDefault();
              selectResult(results[0].path);
            }
          }}
          placeholder="جست‌وجو در CRM"
          value={value}
        />
      </label>

      {isOpen && value.trim() ? (
        <div className="absolute left-0 top-[calc(100%+0.5rem)] z-50 w-[min(28rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl shadow-slate-900/15">
          {term.length < 2 ? (
            <p className="px-3 py-4 text-center text-xs font-bold text-slate-400">
              حداقل دو نویسه وارد کنید.
            </p>
          ) : searchQuery.isLoading ? (
            <p className="px-3 py-4 text-center text-xs font-bold text-slate-400">
              در حال جست‌وجو…
            </p>
          ) : groups.length > 0 ? (
            <div className="max-h-[min(28rem,calc(100vh-8rem))] overflow-y-auto">
              {groups.map((group) => (
                <section className="py-1" key={group.type}>
                  <p className="px-3 pb-1 pt-2 text-[11px] font-black text-slate-400">
                    {titleByType[group.type]}
                  </p>
                  {group.items.map((result) => (
                    <button
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-right transition hover:bg-brand-50"
                      key={`${result.type}-${result.id}`}
                      onClick={() => selectResult(result.path)}
                      type="button"
                    >
                      <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-500">
                        <AppIcon name={iconByType[result.type]} size={17} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-black text-slate-700">
                          {result.title}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-slate-400">
                          {result.subtitle}
                        </span>
                      </span>
                      <AppIcon
                        className="shrink-0 text-slate-300"
                        name="chevron-left"
                        size={16}
                      />
                    </button>
                  ))}
                </section>
              ))}
              {searchQuery.data?.failedSources ? (
                <p className="border-t border-amber-100 bg-amber-50 px-3 py-2 text-[11px] font-bold text-amber-700">
                  بخشی از نتایج در دسترس نبود؛ نتایج قابل‌دسترسی نمایش داده
                  شده‌اند.
                </p>
              ) : null}
            </div>
          ) : (
            <p className="px-3 py-4 text-center text-xs font-bold text-slate-400">
              نتیجه‌ای در سرنخ‌ها، مخاطبان یا سازمان‌ها پیدا نشد.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
};
